import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { Upload, File, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { TierLimitModal } from './TierLimitModal';
import { wouldExceedStorageQuota } from '../utils/storage-quota';
import { fetchExistingProjectResources, isExistingFileName } from '../utils/check-existing-resources';
import { ConfirmDialog } from './ConfirmDialog';

interface FileWithProgress {
  file: File;
  id: string;
  status: 'checking' | 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  resourceId?: string;
}

interface UploadResourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

export const UploadResourcesModal: React.FC<UploadResourcesModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [awaitingConfirmIds, setAwaitingConfirmIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const uploadInProgressRef = useRef(false);
  const filesRef = useRef<FileWithProgress[]>([]);
  const duplicateConfirmedRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  filesRef.current = files;
  isOpenRef.current = isOpen;

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setIsUploading(false);
      setShowDuplicateConfirm(false);
      setAwaitingConfirmIds([]);
      duplicateConfirmedRef.current = false;
    }
  }, [isOpen]);


  const addFiles = useCallback((fileList: FileList | null): void => {
    if (!fileList) return;

    const newFiles: FileWithProgress[] = Array.from(fileList).map((file) => ({
      file,
      id: `${Date.now()}-${Math.random()}`,
      status: 'checking' as const,
      progress: 0,
    }));
    const incomingIds = new Set(newFiles.map((f) => f.id));

    setFiles((prev) => [...prev, ...newFiles]);

    const resolveDuplicates = async (): Promise<void> => {
      const sessionNames = filesRef.current.map((f) => f.file.name);
      const dbMatches = token
        ? await fetchExistingProjectResources(token, projectId, {
            fileNames: newFiles.map((f) => f.file.name),
          })
        : [];
      if (!isOpenRef.current) {
        return;
      }
      const existingNames = [
        ...dbMatches.map((resource) => resource.fileName),
        ...sessionNames,
      ];
      const duplicateIds = new Set(
        newFiles
          .filter((f) => isExistingFileName(f.file.name, existingNames))
          .map((f) => f.id)
      );

      if (duplicateIds.size === 0) {
        setFiles((prev) =>
          prev.map((f) =>
            incomingIds.has(f.id) ? { ...f, status: 'pending' as const } : f
          )
        );
        return;
      }

      setFiles((prev) =>
        prev.map((f) => {
          if (!incomingIds.has(f.id)) {
            return f;
          }
          if (duplicateIds.has(f.id)) {
            return f;
          }
          return { ...f, status: 'pending' as const };
        })
      );
      setAwaitingConfirmIds((prev) => [...prev, ...duplicateIds]);
      setShowDuplicateConfirm(true);
    };

    void resolveDuplicates();
  }, [projectId, token]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    addFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (id: string): void => {
    if (isUploading) return;
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const uploadFile = async (fileWithProgress: FileWithProgress): Promise<void> => {
    if (!token) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithProgress.id
            ? { ...f, status: 'error' as const, error: 'No authentication token' }
            : f
        )
      );
      return;
    }

    try {

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithProgress.id ? { ...f, status: 'uploading' as const, progress: 0 } : f
        )
      );

      const presignedUrlResponse = await fetch(
        `${API_BASE}/api/projects/${projectId}/resources/presigned-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: fileWithProgress.file.name,
            fileSize: fileWithProgress.file.size,
            mimeType: fileWithProgress.file.type || 'application/octet-stream',
          }),
        }
      );

      if (!presignedUrlResponse.ok) {
        const errorText = await presignedUrlResponse.text();
        throw new Error(errorText || `Failed to get presigned URL: ${presignedUrlResponse.status}`);
      }

      const { presignedUrl, s3Key } = await presignedUrlResponse.json();

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setFiles((prev) =>
            prev.map((f) => (f.id === fileWithProgress.id ? { ...f, progress } : f))
          );
        }
      });

      const uploadToS3Promise = new Promise<void>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during S3 upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('S3 upload cancelled'));
        });
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', fileWithProgress.file.type || 'application/octet-stream');
      xhr.send(fileWithProgress.file);

      await uploadToS3Promise;

      const saveMetadataResponse = await fetch(
        `${API_BASE}/api/projects/${projectId}/resources`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: fileWithProgress.file.name,
            fileSize: fileWithProgress.file.size,
            mimeType: fileWithProgress.file.type || 'application/octet-stream',
            s3Key,
          }),
        }
      );

      if (!saveMetadataResponse.ok) {
        const errorData = await saveMetadataResponse.json().catch(() => ({}));
        if (saveMetadataResponse.status === 403 && errorData.errorCode === 'RESOURCE_LIMIT_REACHED') {
          throw new Error('RESOURCE_LIMIT_REACHED');
        }
        const errorText = await saveMetadataResponse.text().catch(() => '');
        throw new Error(errorText || `Failed to save resource metadata: ${saveMetadataResponse.status}`);
      }

      const saveData = await saveMetadataResponse.json();

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithProgress.id
            ? { ...f, status: 'success' as const, progress: 100, resourceId: saveData.id }
            : f
        )
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error instanceof Error && error.message === 'RESOURCE_LIMIT_REACHED') {
        window.dispatchEvent(new CustomEvent('resource-limit-reached'));
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileWithProgress.id
              ? { ...f, status: 'error' as const, error: 'Resource limit reached' }
              : f
          )
        );
      } else {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileWithProgress.id
              ? { ...f, status: 'error' as const, error: error instanceof Error ? error.message : 'Unknown error' }
              : f
          )
        );
      }
    }
  };

  const pendingFileIds = files.filter((f) => f.status === 'pending').map((f) => f.id).join(',');

  useEffect(() => {
    if (isUploading || uploadInProgressRef.current) return;
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    const performUpload = async (): Promise<void> => {
      if (!token) return;

      const totalSize = pendingFiles.reduce((sum, f) => sum + f.file.size, 0);
      const exceedsQuota = await wouldExceedStorageQuota(token, totalSize);
      if (exceedsQuota) {
        setShowStorageLimitModal(true);
        setFiles((prev) =>
          prev.map((f) =>
            pendingFiles.some((pf) => pf.id === f.id)
              ? { ...f, status: 'error' as const, error: 'Storage quota exceeded' }
              : f
          )
        );
        return;
      }

      uploadInProgressRef.current = true;
      setIsUploading(true);
      await Promise.allSettled(pendingFiles.map((f) => uploadFile(f)));
      setIsUploading(false);
      uploadInProgressRef.current = false;
    };

    performUpload();
  }, [pendingFileIds, isUploading, projectId, token]);

  useEffect(() => {
    const allComplete = files.length > 0 && files.every((f) => f.status === 'success' || f.status === 'error');
    const hasSuccess = files.some((f) => f.status === 'success');
    if (allComplete && hasSuccess && !isUploading) {
      const resourceIds = files
        .filter((f): f is FileWithProgress & { resourceId: string } => f.status === 'success' && !!f.resourceId)
        .map((f) => f.resourceId);
      window.dispatchEvent(
        new CustomEvent('resource-uploaded', { detail: { resourceIds, projectId } })
      );
    }
  }, [files.map((f) => `${f.id}-${f.status}-${f.resourceId ?? ''}`).join(','), isUploading, projectId]);

  const awaitingFiles = files.filter((f) => awaitingConfirmIds.includes(f.id));
  const allUploadsComplete = files.length === 0 || files.every((f) => f.status === 'success' || f.status === 'error');
  const hasErrors = files.some((f) => f.status === 'error');
  const canClose = !isUploading && !showDuplicateConfirm && (files.length === 0 || allUploadsComplete);

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      if (canClose) {
        onClose();
      }
    }
  };

  const promoteAwaitingFiles = (): void => {
    duplicateConfirmedRef.current = true;
    const ids = new Set(awaitingConfirmIds);
    setFiles((prev) =>
      prev.map((f) => (ids.has(f.id) ? { ...f, status: 'pending' as const } : f))
    );
    setAwaitingConfirmIds([]);
  };

  const discardAwaitingFiles = (): void => {
    if (duplicateConfirmedRef.current) {
      duplicateConfirmedRef.current = false;
      return;
    }
    const ids = new Set(awaitingConfirmIds);
    setFiles((prev) => prev.filter((f) => !ids.has(f.id)));
    setAwaitingConfirmIds([]);
  };

  const duplicateFileDescription =
    awaitingFiles.length > 1
      ? t('duplicate_resource_files_description').replace(
          '{names}',
          awaitingFiles.map((f) => f.file.name).join(', ')
        )
      : t('duplicate_resource_file_description').replace(
          '{name}',
          awaitingFiles[0]?.file.name ?? ''
        );

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        onInteractOutside={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('Upload resources')}</DialogTitle>
          <DialogDescription>
            {t('Select files to upload to this project')}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-primary bg-muted' : 'border-muted-foreground/25 bg-card'
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-base font-medium mb-2">
            {t('Drag and drop files here')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('or click to select files')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {files.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <h3 className="text-sm font-semibold mb-3">
              {t('Selected files')} ({files.length})
            </h3>
            <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
              {files.map((fileWithProgress) => (
                <div
                  key={fileWithProgress.id}
                  className="flex items-center gap-3 p-3 border rounded-md bg-background"
                >
                  <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {fileWithProgress.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(fileWithProgress.file.size)}
                    </p>
                    {fileWithProgress.status === 'uploading' && (
                      <div className="mt-2 w-full h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${fileWithProgress.progress}%` }}
                        />
                      </div>
                    )}
                    {fileWithProgress.status === 'error' && fileWithProgress.error && (
                      <p className="mt-1 text-xs text-destructive">
                        {fileWithProgress.error}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(fileWithProgress.status === 'uploading' || fileWithProgress.status === 'checking') && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    {fileWithProgress.status === 'success' && (
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    )}
                    {fileWithProgress.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mt-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground text-center">
              {t('Uploading files... Please wait.')}
            </p>
          </div>
        )}

        <DialogFooter className="mt-6">
          {canClose && (
            <Button
              type="button"
              onClick={onClose}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {hasErrors ? t('cancel') : t('Done')}
              <DialogPrimaryShortcut className="ml-0.5" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <TierLimitModal
        open={showStorageLimitModal}
        onOpenChange={setShowStorageLimitModal}
        limitType="storage"
      />
    </Dialog>
    <ConfirmDialog
      open={showDuplicateConfirm}
      onOpenChange={(open) => {
        if (!open) {
          discardAwaitingFiles();
          setShowDuplicateConfirm(false);
        }
      }}
      destructive={false}
      title={
        awaitingFiles.length > 1
          ? t('duplicate_resource_files_title')
          : t('duplicate_resource_file_title')
      }
      description={duplicateFileDescription}
      confirmLabel={t('duplicate_resource_upload_anyway')}
      onConfirm={promoteAwaitingFiles}
    />
    </>
  );
};
